import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize } from "rxjs/operators";
import { MessageService } from "common";
import { DisclosureDto } from "../../agreement/agreement.data";
import { AgreementDisclosureFacade } from "../domain/+state/agreement-disclosure.facade";

/**
 * One disclosure on the in-house agreement: initialled in place, or, when it carries its own text, accepted from
 * the disclosure pop-up shown above its place in the agreement.
 */
@UntilDestroy()
@Component({
  selector: "ifp-agreement-disclosure",
  templateUrl: "./agreement-disclosure.component.html",
  styleUrls: ["./agreement-disclosure.component.scss"],
  standalone: false,
})
export class AgreementDisclosureComponent implements OnChanges, OnDestroy {
  /** How long a highlight stays on; matches the animation in the stylesheet. */
  static readonly HighlightDuration = 1000;

  @Input() disclosure: DisclosureDto;

  /** Shown in place of the checkbox once the disclosure is accepted. */
  @Input() signerInitials: string;

  /** Asked before every acceptance; false stops it, the guard reporting why. */
  @Input() acceptGuard: () => boolean;

  /** This disclosure's place among the agreement's disclosures, for the pop-up's "1 of 4". */
  @Input() position: number;

  @Input() total: number;

  /** Emits the disclosure code once its acceptance is recorded. */
  @Output() accepted = new EventEmitter<string>();

  /** Emits the disclosure code when its pop-up opens, so any other open one can close. */
  @Output() popupOpened = new EventEmitter<string>();

  acceptedOnUtc: Date | null = null;

  /** The disclosure carries its own text, so it is accepted from a pop-up rather than initialled in place. */
  isPopup = false;

  popupOpen = false;

  /** The pop-up text; server-rendered from the product template, as the original pop-up displayed it. */
  popupText: SafeHtml;

  /** True while the acceptance is in flight; a spinner stands in for the checkbox meanwhile. */
  submitting = false;

  /** Briefly set when the signer is sent here, so the disclosure stands out once scrolled into view. */
  highlighted = false;

  private highlightTimer: ReturnType<typeof setTimeout>;

  constructor(
    private readonly facade: AgreementDisclosureFacade,
    private readonly messageService: MessageService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnChanges(): void {
    this.isPopup = !!this.disclosure?.text?.trim();
    this.popupText = this.isPopup ? this.sanitizer.bypassSecurityTrustHtml(this.disclosure.text) : null;
    this.acceptedOnUtc = this.disclosure?.acceptedOnUtc ? new Date(this.disclosure.acceptedOnUtc) : null;
  }

  initial(change?: MatCheckboxChange): void {
    if (this.acceptedOnUtc || this.submitting) return;

    if (!this.isAcceptAllowed()) {
      // The click has already ticked the box; a refused acceptance must leave it unticked.
      if (change) change.source.checked = false;
      return;
    }

    this.accept();
  }

  /** Marks this disclosure as where the signer is: opens its pop-up, or highlights its checkbox. */
  focus(): void {
    if (this.isPopup) this.openPopup();
    else this.highlight();
  }

  openPopup(): void {
    if (!this.isPopup || this.acceptedOnUtc) return;

    this.popupOpen = true;
    this.popupOpened.emit(this.disclosure.code);
  }

  closePopup(): void {
    this.popupOpen = false;
  }

  highlight(): void {
    clearTimeout(this.highlightTimer);
    this.highlighted = true;
    this.highlightTimer = setTimeout(() => (this.highlighted = false), AgreementDisclosureComponent.HighlightDuration);
  }

  private isAcceptAllowed(): boolean {
    return this.acceptGuard?.() ?? true;
  }

  private accept(): void {
    this.submitting = true;

    this.facade
      .acceptDisclosure$(this.disclosure.code)
      .pipe(
        finalize(() => (this.submitting = false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (acceptance) => {
          this.acceptedOnUtc = acceptance.acceptedOnUtc;
          this.popupOpen = false;
          this.accepted.emit(this.disclosure.code);
        },
        error: (error) => this.messageService.error(error),
      });
  }

  ngOnDestroy(): void {
    clearTimeout(this.highlightTimer);
  }
}
